import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentInfo, Product, ProductsService } from '@ubie/core';

@Component({
  selector: 'acl-products-list',
  standalone: false,
  templateUrl: './products-list.component.html',
  styleUrl: './products-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsListComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly productsService = inject(ProductsService);
  // 1. Create internal signals
  readonly _products = signal<Product[]>([]);
  readonly _title = signal<string>('');
  readonly _intro = signal<string>('');

  // 2. Use standard @Input setters to update the signals
  // This allows ModalController to assign values without breaking the signals
  @Input() set products(value: Product[]) {
    this._products.set(value);
  }
  @Input() set title(value: string) {
    this._title.set(value);
  }
  @Input() set intro(value: string) {
    this._intro.set(value);
  }

  readonly hasNoProducts = computed(() => this._products()?.length === 0);

  readonly selectedProduct = signal<Product | null>(null);

  readonly selectedProductAmount = computed(
    () => this.selectedProduct()?.amount ?? 0,
  );

  readonly selectedProductName = computed(
    () => this.selectedProduct()?.name ?? '',
  );

  readonly selectedProductRecurring = computed(
    () => this.selectedProduct()?.recurring ?? false,
  );

  readonly selectedProductPrice = computed(
    () =>
      (this.selectedProduct()?.price ?? 0) *
      (this.selectedProduct()?.amount ?? 0),
  );
  form: FormGroup;

  constructor() {
    this.form = this.formBuilder.group({ amount: [15, Validators.required] });
  }

  onProductClicked(product: Product): void {
    this.selectedProduct.set({
      ...product,
      amount: this.form?.get('amount')?.value,
    });
  }
  onBackClicked(): void {
    this.selectedProduct.set(null);
  }

  /**
   * Buy was clicked
   * @param product
   */
  onBuyClicked(): void {
    const product = this.selectedProduct();

    if (product === null) {
      return;
    }

    // create payment info to send to mollie
    const info: PaymentInfo = {
      redirectUrl:
        location.protocol +
        '//' +
        location.hostname +
        '/order/[orderNo]/status',
      orderDescription: 'IDB order #[orderNo]',
    };

    if (product.uid === undefined || product.amount === undefined) {
      return;
    }

    this.productsService
      .buyProduct(product.uid, product.amount, info)
      .subscribe({
        next: (result) => {
          if (result.paymentUrl !== null && result.paymentUrl !== undefined) {
            window.location.href = result.paymentUrl;
          } else {
            // TODO show error message
          }
        },
        error: (error) => {
          // TODO show error message
        },
      });
  }
}
